import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, ArrowRight, LayoutDashboard, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LOGO_URL } from "@/constants";

const BookingResult: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const [count, setCount] = useState(5);

    const status = searchParams.get("status");
    const isCancelled = status === "cancelled";
    const isConfirmed = status === "confirmed";

    useEffect(() => {
        if (!isCancelled && !isConfirmed) {
            navigate("/", { replace: true });
            return;
        }

        const timer = setInterval(() => {
            setCount((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    navigate("/", { replace: true });
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [navigate, isCancelled, isConfirmed]);

    if (!isCancelled && !isConfirmed) return null;

    const bgColor = isCancelled ? "bg-red-100/40" : "bg-green-100/40";
    const iconBg = isCancelled ? "bg-red-500" : "bg-green-500";
    const iconShadow = isCancelled ? "shadow-red-500/30" : "shadow-green-500/30";
    const iconRipple = isCancelled ? "bg-red-400" : "bg-green-400";

    return (
        <div className="min-h-screen bg-white font-sans selection:bg-clip-100 relative overflow-hidden flex items-center justify-center">
            {/* Animated Background Blobs */}
            <div className="fixed inset-0 pointer-events-none z-0">
                <motion.div
                    animate={{
                        x: [0, 100, 0],
                        y: [0, -50, 0],
                        scale: [1, 1.2, 1],
                    }}
                    transition={{
                        opacity: { duration: 1.5, ease: "easeOut" },
                        default: { duration: 20, repeat: Infinity, ease: "easeInOut" }
                    }}
                    className={`absolute top-[-10%] left-[-10%] w-[500px] h-[500px] ${bgColor} rounded-full blur-[100px]`}
                />
                <motion.div
                    animate={{
                        x: [0, -100, 0],
                        y: [0, 100, 0],
                        scale: [1, 1.3, 1],
                    }}
                    transition={{
                        opacity: { duration: 1.5, ease: "easeOut" },
                        default: { duration: 25, repeat: Infinity, ease: "easeInOut" }
                    }}
                    className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-clip-100/30 rounded-full blur-[120px]"
                />
            </div>

            {/* Navbar Minimal (Solo Logo) */}
            <nav className="absolute top-0 left-0 right-0 p-6 flex justify-center z-50">
                <img src={LOGO_URL} alt="Clip Logo" className="h-8 w-auto object-contain" />
            </nav>

            <div className="relative z-10 w-full max-w-lg px-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white/80 backdrop-blur-xl border border-gray-100 shadow-2xl shadow-gray-200/50 rounded-3xl p-8 md:p-12 text-center"
                >
                    {/* Icon Animation */}
                    <div className="mb-8 flex justify-center">
                        <div className="relative">
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 260,
                                    damping: 20,
                                    delay: 0.1,
                                }}
                                className={`w-24 h-24 ${iconBg} rounded-full flex items-center justify-center shadow-lg ${iconShadow} relative z-10`}
                            >
                                {isCancelled ? (
                                    <X className="text-white w-12 h-12" strokeWidth={3} />
                                ) : (
                                    <Check className="text-white w-12 h-12" strokeWidth={3} />
                                )}
                            </motion.div>
                            {/* Ripple effect */}
                            <motion.div
                                animate={{
                                    scale: [1, 1.5],
                                    opacity: [0, 0.3, 0]
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeOut"
                                }}
                                className={`absolute inset-0 ${iconRipple} rounded-full z-0`}
                            />
                        </div>
                    </div>

                    <motion.h1
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-3xl font-black text-gray-900 mb-4 tracking-tight"
                    >
                        {isCancelled ? t('booking_result.cancelled_title') : t('booking_result.confirmed_title')}
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="text-gray-500 text-lg mb-8 leading-relaxed"
                    >
                        {isCancelled ? t('booking_result.cancelled_message') : t('booking_result.confirmed_message')}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="space-y-4"
                    >
                        <button
                            onClick={() => navigate("/", { replace: true })}
                            className="w-full py-4 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center gap-2 group"
                        >
                            <LayoutDashboard size={20} />
                            {t('booking_result.home_btn')}
                            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </button>

                        <p className="text-sm text-gray-400 font-medium flex items-center justify-center gap-2">
                            <Loader2 size={14} className="animate-spin" />
                            {t('payment_success.redirect', { count })}
                        </p>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};

export default BookingResult;
